import { Money } from 'grommet-icons';
import React, { useMemo, useState } from 'react';
import * as XLSX from 'xlsx';

import { SubHeader } from '../../components';
import { useUsers } from '../../context/users';
import { LedgerEntry } from '../../context/model/types';
import LedgerEntryModal from '../../components/Modals/LedgerEntryModal';

type Tab = 'entries' | 'payments';

const PAID_STATUSES = ['Paid', 'Unpaid'] as const;
const PAID_STATUS_COLORS: Record<string, { bg: string; fg: string }> = {
  'Paid': { bg: '#D1FAE5', fg: '#065F46' },
  'Unpaid': { bg: '#FEE2E2', fg: '#991B1B' },
};

const money = (n: number, decimals = 2) =>
  `$${n.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}`;

// 'YYYY-MM' -> "May 25"
const monthLabel = (m?: string | null) => {
  if (!m) return '—';
  const [y, mo] = m.split('-');
  if (!y || !mo) return m;
  return new Date(Number(y), Number(mo) - 1, 1)
    .toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const Ledger = (): JSX.Element => {
  const { ledgerEntries } = useUsers();
  const [tab, setTab] = useState<Tab>('entries');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<LedgerEntry | null>(null);
  const [paidFilter, setPaidFilter] = useState<Set<string>>(new Set(PAID_STATUSES));

  const togglePaid = (s: string) => {
    setPaidFilter(prev => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  const entries = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = q
      ? ledgerEntries.filter(e =>
          e.name?.toLowerCase().includes(q) ||
          e.type?.toLowerCase().includes(q) ||
          e.notes?.toLowerCase().includes(q)
        )
      : ledgerEntries;
    if (paidFilter.size < PAID_STATUSES.length) {
      base = base.filter(e => paidFilter.has(e.paid === false ? 'Unpaid' : 'Paid'));
    }
    // Newest first by date.
    return [...base].sort((a, b) => (b.date ?? '').localeCompare(a.date ?? ''));
  }, [ledgerEntries, search, paidFilter]);

  const collected = useMemo(
    () => ledgerEntries.filter(e => e.paid !== false).reduce((s, e) => s + (e.amount ?? 0), 0),
    [ledgerEntries],
  );
  const outstanding = useMemo(
    () => ledgerEntries.filter(e => e.paid === false).reduce((s, e) => s + (e.amount ?? 0), 0),
    [ledgerEntries],
  );

  // ── Payments pivot: customer × month ──
  const pivot = useMemo(() => {
    const months = Array.from(
      new Set(ledgerEntries.map(e => e.month).filter(Boolean) as string[]),
    ).sort();
    const names = Array.from(
      new Set(ledgerEntries.map(e => (e.name || '').trim()).filter(Boolean)),
    ).sort();
    // cell[name][month] = sum
    const cell: Record<string, Record<string, number>> = {};
    const monthTotal: Record<string, number> = {};
    for (const e of ledgerEntries) {
      const n = (e.name || '').trim();
      const m = e.month;
      if (!n || !m) continue;
      cell[n] = cell[n] || {};
      cell[n][m] = (cell[n][m] || 0) + (e.amount ?? 0);
      monthTotal[m] = (monthTotal[m] || 0) + (e.amount ?? 0);
    }
    return { months, names, cell, monthTotal };
  }, [ledgerEntries]);

  const today = new Date().toISOString().slice(0, 10);

  const exportEntries = () => {
    const rows = entries.map(e => ({
      Date: e.date || '',
      Name: e.name || '',
      Amount: e.amount ?? '',
      Type: e.type || '',
      Month: e.month || '',
      Paid: e.paid === false ? 'Unpaid' : 'Paid',
      Notes: e.notes || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ledger');
    XLSX.writeFile(wb, `ledger-entries-${today}.xlsx`);
  };

  const exportPayments = () => {
    const rows = pivot.names.map(n => {
      const row: Record<string, string | number> = { Customer: n };
      for (const m of pivot.months) row[m] = pivot.cell[n]?.[m] ?? 0;
      return row;
    });
    const totalRow: Record<string, string | number> = { Customer: 'Total' };
    for (const m of pivot.months) totalRow[m] = pivot.monthTotal[m] ?? 0;
    rows.push(totalRow);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payments');
    XLSX.writeFile(wb, `ledger-payments-${today}.xlsx`);
  };

  return (
    <>
      <SubHeader title="Ledger" icon={<Money />} />
      <div style={{ padding: '16px 24px', fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        {/* tab toggle */}
        <div style={{ display: 'flex', marginBottom: 12, borderBottom: '1px solid #E5E7EB' }}>
          <TabButton active={tab === 'entries'} onClick={() => setTab('entries')}>
            Entries <span style={countStyle}>{ledgerEntries.length}</span>
          </TabButton>
          <TabButton active={tab === 'payments'} onClick={() => setTab('payments')}>
            Payments
          </TabButton>
        </div>

        {/* summary */}
        <div style={{ display: 'flex', gap: 24, marginBottom: 12, fontSize: 13 }}>
          <span style={{ color: '#065F46' }}>Collected: <strong>{money(collected)}</strong></span>
          <span style={{ color: '#991B1B' }}>Outstanding: <strong>{money(outstanding)}</strong></span>
        </div>

        {tab === 'entries' ? (
          <>
            {/* toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              <input
                type="search"
                placeholder="Search name, type, notes…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{
                  padding: '8px 12px', borderRadius: 6, border: '1px solid #D1D5DB',
                  fontSize: 13, width: 260, outline: 'none',
                }}
              />
              <span style={{ fontSize: 13, color: '#6B7280' }}>
                {entries.length} entr{entries.length !== 1 ? 'ies' : 'y'}
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {PAID_STATUSES.map(s => {
                  const c = PAID_STATUS_COLORS[s];
                  return (
                    <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer', userSelect: 'none' }}>
                      <input
                        type="checkbox"
                        checked={paidFilter.has(s)}
                        onChange={() => togglePaid(s)}
                        style={{ accentColor: '#7B1FA2', cursor: 'pointer' }}
                      />
                      <span style={{
                        display: 'inline-block', padding: '1px 6px', borderRadius: 4,
                        fontSize: 11, fontWeight: 600, backgroundColor: c.bg, color: c.fg,
                      }}>
                        {s}
                      </span>
                    </label>
                  );
                })}
              </div>
              <button
                type="button"
                onClick={exportEntries}
                style={{
                  marginLeft: 'auto',
                  padding: '8px 14px', borderRadius: 6,
                  border: '1px solid #7B1FA2', background: '#fff', color: '#7B1FA2',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                Export
              </button>
              <button
                onClick={() => setSelected({ id: '' } as LedgerEntry)}
                style={{
                  padding: '8px 14px', borderRadius: 6, border: 'none',
                  background: '#7B1FA2', color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                + Add entry
              </button>
            </div>

            <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #E5E7EB' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Name</th>
                    <th style={thStyle}>Amount</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Month</th>
                    <th style={thStyle}>Paid</th>
                    <th style={thStyle}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.length === 0 && (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
                        No ledger entries
                      </td>
                    </tr>
                  )}
                  {entries.map(e => (
                    <tr
                      key={e.id}
                      onClick={() => setSelected(e)}
                      style={{ borderBottom: '1px solid #F3F4F6', cursor: 'pointer' }}
                      onMouseEnter={ev => (ev.currentTarget.style.backgroundColor = '#F9FAFB')}
                      onMouseLeave={ev => (ev.currentTarget.style.backgroundColor = '')}
                    >
                      <td style={tdStyle}>{e.date || '—'}</td>
                      <td style={tdStyle}>{e.name || '—'}</td>
                      <td style={tdStyle}>{e.amount != null ? money(e.amount) : '—'}</td>
                      <td style={tdStyle}>{e.type || '—'}</td>
                      <td style={tdStyle}>{monthLabel(e.month)}</td>
                      <td style={tdStyle}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', borderRadius: 999,
                          fontSize: 11, fontWeight: 500,
                          backgroundColor: e.paid === false ? '#FEE2E2' : '#D1FAE5',
                          color: e.paid === false ? '#991B1B' : '#065F46',
                        }}>
                          {e.paid === false ? 'unpaid' : 'paid'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: '#6B7280', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.notes ?? ''}>
                        {e.notes || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        ) : (
          /* ── Payments pivot ── */
          <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <button
              type="button"
              onClick={exportPayments}
              style={{
                marginLeft: 'auto',
                padding: '8px 14px', borderRadius: 6,
                border: '1px solid #7B1FA2', background: '#fff', color: '#7B1FA2',
                fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              Export
            </button>
          </div>
          <div style={{ overflowX: 'auto', borderRadius: 8, border: '1px solid #E5E7EB' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#F9FAFB', borderBottom: '1px solid #E5E7EB' }}>
                  <th style={{ ...thStyle, position: 'sticky', left: 0, background: '#F9FAFB' }}>Customer</th>
                  {pivot.months.map(m => (
                    <th key={m} style={{ ...thStyle, textAlign: 'right' }}>{monthLabel(m)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pivot.names.length === 0 && (
                  <tr>
                    <td colSpan={pivot.months.length + 1} style={{ textAlign: 'center', padding: 24, color: '#9CA3AF' }}>
                      No payments yet
                    </td>
                  </tr>
                )}
                {pivot.names.length > 0 && (
                  <tr style={{ borderBottom: '2px solid #E5E7EB', fontWeight: 700, background: '#FCFAFF' }}>
                    <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#FCFAFF', fontWeight: 700 }}>Total</td>
                    {pivot.months.map(m => (
                      <td key={m} style={{ ...tdStyle, textAlign: 'right', fontWeight: 700 }}>
                        {pivot.monthTotal[m] ? money(pivot.monthTotal[m], 0) : '—'}
                      </td>
                    ))}
                  </tr>
                )}
                {pivot.names.map(n => (
                  <tr key={n} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ ...tdStyle, position: 'sticky', left: 0, background: '#fff' }}>{n}</td>
                    {pivot.months.map(m => {
                      const v = pivot.cell[n]?.[m] ?? 0;
                      return (
                        <td key={m} style={{ ...tdStyle, textAlign: 'right', color: v ? '#111827' : '#D1D5DB' }}>
                          {v ? money(v, 0) : '0'}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {selected && (
        <LedgerEntryModal entry={selected} setShow={() => setSelected(null)} />
      )}
    </>
  );
};

const TabButton: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({
  active, onClick, children,
}) => (
  <button
    onClick={onClick}
    style={{
      padding: '10px 16px', border: 'none',
      borderBottom: active ? '2px solid #7B1FA2' : '2px solid transparent',
      background: 'transparent', fontSize: 14,
      fontWeight: active ? 600 : 500,
      color: active ? '#111827' : '#6B7280',
      cursor: 'pointer', marginBottom: -1,
    }}
  >
    {children}
  </button>
);

const countStyle: React.CSSProperties = {
  marginLeft: 6, fontSize: 12, color: '#9CA3AF', fontWeight: 500,
};

const thStyle: React.CSSProperties = {
  padding: '10px 14px', textAlign: 'left', fontWeight: 600,
  color: '#374151', whiteSpace: 'nowrap', userSelect: 'none',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', color: '#111827', whiteSpace: 'nowrap',
};

export default Ledger;
